// Plain-language freelance services agreement for /tools/freelance-contract-template.
// A starting template, not legal advice: every export carries that notice, and the
// page tells people to have a lawyer review it. Pure functions; output is plain text
// sections (rendered with text interpolation). exportHtml escapes every value.

export const NOT_LEGAL_ADVICE =
  'Template only, not legal advice. Laws on contracts, intellectual property, late fees and liability differ by state and country. Have a qualified lawyer review this agreement before you rely on it, and delete this notice once they have.';

const text = (value, max = 400) => String(value ?? '').replace(/[<>]/g, '').replace(/[ \t]+/g, ' ').trim().slice(0, max);
const lines = (value, max = 20) => String(value ?? '')
  .split(/\n+/)
  .map((line) => text(line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, ''), 200))
  .filter(Boolean)
  .slice(0, max);
const int = (value, min, max, fallback) => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
};
const amountNumber = (value) => {
  const n = Number(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
};

export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

export function money(amount, currency = 'USD') {
  const code = CURRENCIES.includes(currency) ? currency : 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);
}

const dateText = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

export function normalizeContract(input = {}) {
  return {
    provider: text(input.provider, 120),
    client: text(input.client, 120),
    effective: dateText(input.effective),
    start: dateText(input.start),
    end: dateText(input.end),
    services: text(input.services, 600),
    deliverables: lines(input.deliverables),
    exclusions: lines(input.exclusions),
    feeType: ['fixed', 'hourly', 'retainer'].includes(input.feeType) ? input.feeType : 'fixed',
    amount: amountNumber(input.amount),
    hours: int(input.hours, 0, 10000, 0),
    currency: CURRENCIES.includes(input.currency) ? input.currency : 'USD',
    deposit: int(input.deposit, 0, 100, 0),
    netDays: int(input.netDays, 0, 120, 14),
    lateFee: Math.min(10, Math.max(0, Number(input.lateFee) || 0)),
    revisions: int(input.revisions, 0, 20, 2),
    noticeDays: int(input.noticeDays, 0, 180, 14),
    killFee: int(input.killFee, 0, 100, 0),
    ownership: input.ownership === 'license' ? 'license' : 'assign',
    portfolio: input.portfolio !== false,
    liabilityCap: input.liabilityCap !== false,
    law: text(input.law, 80),
  };
}

export function buildContract(input = {}) {
  const c = normalizeContract(input);
  const freelancer = c.provider || '[Your name or business]';
  const client = c.client || '[Client name or business]';
  const fee = c.amount ? money(c.amount, c.currency) : '[amount]';
  const sections = [];
  const add = (heading, ...paragraphs) => sections.push({ heading, paragraphs: paragraphs.flat().filter(Boolean) });

  add('Parties',
    `This Freelance Services Agreement (the "Agreement") is between ${freelancer} ("Freelancer") and ${client} ("Client")${c.effective ? `, effective ${c.effective}` : ''}.`);

  add('Services and deliverables',
    `Freelancer will provide the following services: ${c.services || '[describe the services]'}.`.replace(/\.\.$/, '.'),
    c.deliverables.length ? ['The deliverables are:', ...c.deliverables.map((d) => `• ${d}`)] : '',
    c.exclusions.length ? ['Not included in this Agreement:', ...c.exclusions.map((d) => `• ${d}`)] : '',
    'Work outside this scope needs a written change order (email is enough) that both parties agree to before the work starts, including any added fee and timeline.');

  add('Timeline',
    `${c.start ? `Work starts on ${c.start}` : 'Work starts on the date both parties sign'}${c.end ? ` and is expected to be completed by ${c.end}` : ''}.`,
    'The timeline depends on Client providing content, feedback and approvals when needed. If either party is delayed, the schedule moves by a reasonable amount and the parties will agree the new dates in writing.');

  const payment = [];
  if (c.feeType === 'fixed') payment.push(`Client will pay Freelancer a fixed fee of ${fee} for the services.`);
  if (c.feeType === 'hourly') payment.push(
    `Client will pay Freelancer ${fee} per hour, invoiced every two weeks for the time worked.${c.hours ? ` The current estimate is ${c.hours} hours.` : ''}`,
    'Freelancer will tell Client in writing before work goes more than 10% over the estimate, and will continue only with Client’s approval.');
  if (c.feeType === 'retainer') payment.push(
    `Client will pay Freelancer ${fee} per month for the services, invoiced at the start of each month.`,
    'Unused time does not carry over to the next month unless both parties agree in writing.');
  if (c.deposit) payment.push(c.feeType === 'fixed' && c.amount
    ? `A deposit of ${c.deposit}% (${money(Math.round(c.amount * c.deposit) / 100, c.currency)}) is due before work starts and is credited to the final invoice.`
    : `A deposit of ${c.deposit}% of the estimated fee is due before work starts and is credited to the final invoice.`);
  payment.push(c.netDays ? `Invoices are due within ${c.netDays} days of the invoice date.` : 'Invoices are due on receipt.');
  if (c.lateFee) payment.push(`Overdue amounts may incur a late fee of ${c.lateFee}% per month, or the highest rate permitted by law if that is lower.`);
  payment.push('Expenses that Client approves in advance (for example stock assets, fonts or software licenses bought for the project) are billed at cost.');
  add('Fees and payment', payment);

  const revisionsText = {
    fixed: c.revisions
      ? `The fee includes ${c.revisions} round${c.revisions === 1 ? '' : 's'} of revisions on each deliverable. Further revisions are billed as a change order.`
      : 'Revisions are billed as a change order.',
    hourly: 'Revisions are billed at the hourly rate, like any other time worked.',
    retainer: 'Revisions count toward the monthly retainer, like any other work.',
  };
  add('Revisions', revisionsText[c.feeType]);

  add('Client responsibilities',
    'Client will provide the content, access and information Freelancer needs, give feedback within a reasonable time, and name one person who can approve work.');

  add('Ownership and use of the work',
    c.ownership === 'assign'
      ? 'Once Client has paid all fees under this Agreement in full, Freelancer transfers to Client the rights in the final deliverables. Until then, Freelancer keeps those rights.'
      : 'Freelancer keeps ownership of the deliverables. Once Client has paid all fees under this Agreement in full, Client may use the final deliverables for its own business, without time limit, but may not resell or license them to others.',
    'Freelancer keeps ownership of tools, templates, code, methods and materials created before or outside this project. Where any of these are part of a deliverable, Client may use them as part of that deliverable.',
    'Third-party materials (such as fonts, stock images or plugins) are covered by their own licenses, which Client must follow.',
    c.portfolio ? 'Freelancer may show the work in a portfolio or case study once it is public, unless Client asks in writing for it to stay private.' : '');

  add('Confidentiality',
    'Each party will keep the other’s confidential information private and use it only for this project. This does not cover information that is already public or that a party is required by law to disclose. This section continues after the Agreement ends.');

  add('Independent contractor',
    'Freelancer is an independent contractor, not an employee. Freelancer chooses how and when to do the work, provides their own equipment, and is responsible for their own taxes and insurance.');

  add('Ending the Agreement',
    `Either party may end this Agreement with ${c.noticeDays ? `${c.noticeDays} days’` : ''} written notice${c.noticeDays ? '' : ' at any time'}.`.replace(' with  written', ' with written'),
    `If the Agreement ends early, Client pays for the work done up to the end date${c.killFee && c.feeType === 'fixed' ? `, plus ${c.killFee}% of the remaining unpaid fixed fee` : ''}, and Freelancer delivers the completed work once that payment is made.`);

  if (c.liabilityCap) add('Limitation of liability',
    'Each party’s total liability under this Agreement is limited to the fees paid or payable under it. Neither party is liable for indirect or consequential losses, such as lost profits. These limits do not apply to unpaid fees, breach of confidentiality, or anything that cannot be limited by law.');

  add('General',
    `${c.law ? `This Agreement is governed by the laws of ${c.law}. ` : ''}The parties will first try to resolve any disagreement by talking in good faith.`,
    'This Agreement is the whole agreement between the parties about this project. Changes must be in writing and agreed by both parties. It may be signed electronically and in separate copies.');

  const signatures = [freelancer, client];
  return { title: 'Freelance Services Agreement', notice: NOT_LEGAL_ADVICE, sections, signatures, checklist: checklist(c) };
}

// Gaps worth fixing before sending; shown beside the preview, never in the document.
function checklist(c) {
  const items = [];
  if (!c.provider || !c.client) items.push('Add both parties’ legal names.');
  if (!c.services) items.push('Describe the services.');
  if (!c.deliverables.length) items.push('List the deliverables so “done” is clear.');
  if (!c.amount) items.push('Set the fee.');
  if (!c.deposit && c.feeType === 'fixed') items.push('Consider a deposit before work starts.');
  if (!c.law) items.push('Add the governing state or country.');
  return items;
}

export function contractText(contract) {
  const out = [contract.notice, '', contract.title.toUpperCase(), ''];
  contract.sections.forEach((section, i) => {
    out.push(`${i + 1}. ${section.heading}`);
    section.paragraphs.forEach((p) => out.push(Array.isArray(p) ? p.join('\n') : p));
    out.push('');
  });
  for (const party of contract.signatures) out.push(`${party}`, 'Signature: ____________________   Date: ____________', '');
  return out.join('\n').trim();
}

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Standalone HTML for print-to-PDF and for Word (.doc), with every value escaped.
export function contractHtml(contract) {
  const body = contract.sections.map((section, i) => `<h2>${i + 1}. ${escapeHtml(section.heading)}</h2>${section.paragraphs.map((p) => (Array.isArray(p)
    ? `<p>${escapeHtml(p[0])}</p><ul>${p.slice(1).map((li) => `<li>${escapeHtml(li.replace(/^• /, ''))}</li>`).join('')}</ul>`
    : `<p>${escapeHtml(p)}</p>`)).join('')}`).join('');
  const signatures = contract.signatures.map((party) => `<div class="sig"><p><strong>${escapeHtml(party)}</strong></p><p>Signature: ______________________________</p><p>Date: ______________</p></div>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(contract.title)}</title>`
    + '<style>body{font-family:Georgia,"Times New Roman",serif;max-width:720px;margin:40px auto;padding:0 24px;color:#111;line-height:1.55;font-size:12pt}h1{font-size:18pt;text-align:center}h2{font-size:12.5pt;margin:18px 0 6px}.notice{border:1px solid #b45309;background:#fffbeb;padding:10px 12px;font-family:Arial,sans-serif;font-size:9.5pt}.sig{margin-top:28px}@media print{.notice{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>'
    + `</head><body><p class="notice">${escapeHtml(contract.notice)}</p><h1>${escapeHtml(contract.title)}</h1>${body}${signatures}</body></html>`;
}
